/**
 * Produces display-only closed geometry for a perimeter whose captured lengths
 * do not sum to an exact loop. Canonical walls and measurements are never
 * mutated; section/layout/export consumers continue to read the model.
 */
import { directionOf } from '../geometry/angles.js';
import { advance, distance } from '../geometry/vector.js';

const SINGULAR = 1e-6;

/**
 * Reconciles the smallest possible set of visible wall-length deltas needed to
 * close a complete perimeter. Opening segments keep their displayed span; only
 * physical walls absorb visual variance. Open runs and one/two-wall sketches
 * are returned unchanged by identity.
 */
export function visualSnapClosedWalls(walls, { enabled = true, openChain = false } = {}) {
  if (!enabled || openChain || !Array.isArray(walls) || walls.length < 3) return walls;
  const adjustable = walls.filter((wall) => (
    (wall.segmentKind ?? 'wall') === 'wall' && wall.snapLengthLocked !== true
  ));
  // Two non-parallel unlocked walls are sufficient to absorb the X/Y closure
  // residual. One wall (or two parallel walls) is intentionally left alone:
  // respecting the designer's locks is more important than forcing a loop.
  if (adjustable.length < 2) return walls;

  let sumX = 0;
  let sumY = 0;
  for (const wall of walls) {
    const d = directionOf(wall.internalAngle);
    const lengthPx = distance(wall.start, wall.end);
    sumX += d.x * lengthPx;
    sumY += d.y * lengthPx;
  }
  if (Math.hypot(sumX, sumY) < 0.01) return walls;

  let m00 = 0;
  let m01 = 0;
  let m11 = 0;
  for (const wall of adjustable) {
    const d = directionOf(wall.internalAngle);
    m00 += d.x * d.x;
    m01 += d.x * d.y;
    m11 += d.y * d.y;
  }
  const det = m00 * m11 - m01 * m01;
  if (Math.abs(det) <= SINGULAR) return walls;

  const gapX = -sumX;
  const gapY = -sumY;
  const lambdaX = (m11 * gapX - m01 * gapY) / det;
  const lambdaY = (-m01 * gapX + m00 * gapY) / det;
  const displayLengths = new Map();
  for (const wall of walls) {
    const current = distance(wall.start, wall.end);
    if ((wall.segmentKind ?? 'wall') !== 'wall' || wall.snapLengthLocked === true) {
      displayLengths.set(wall.id, current);
      continue;
    }
    const d = directionOf(wall.internalAngle);
    const reconciled = current + d.x * lambdaX + d.y * lambdaY;
    if (!(reconciled > 0)) return walls;
    displayLengths.set(wall.id, reconciled);
  }

  let cursor = { ...walls[0].start };
  return walls.map((wall) => {
    const start = { ...cursor };
    const end = advance(start, wall.internalAngle, displayLengths.get(wall.id));
    cursor = { ...end };
    return { ...wall, start, end };
  });
}

/**
 * The factor that maps a wall's ROOM UNITS onto the wall AS DRAWN (RS-1).
 *
 * Snap closure leaves a wall with two lengths. The STATED length is the
 * measurement — what the table shows and what gets built. The DISPLAYED length
 * is what the drawing uses so the room closes on 45° angles. A wall can state
 * 100" and draw as 95".
 *
 * Everything sitting ON that wall — sections, obstructions, the gaps between
 * them — is positioned and sized in stated inches, because that is what gets
 * built. Drawn at true scale onto a wall of a different drawn length it lands
 * in the wrong place, and the error accumulates toward the far end: on a wall
 * stated 120" and displayed 123" the 3/4" end reveal rendered as 3 3/4"
 * (owner, 2026-08-26). Multiplying every ALONG-WALL distance by this factor
 * puts the run back in true relative proportion to the wall it is drawn on.
 *
 * Deliberately narrow in what it corrects:
 *
 * - ALONG-WALL distances only. Depths run ACROSS the wall, where closure bends
 *   nothing, so they stay true.
 * - Gaps take the factor with everything else. Held at true size, the sections
 *   would have to absorb the entire correction and the spacing error would
 *   simply move somewhere else.
 * - Nothing STORED and no LABEL is touched. A section drawn at 25.3" still
 *   reads 26 5/8". Scaling a label would be actively dangerous — the number is
 *   what someone builds from.
 * - The FRONT ELEVATION gets none of this. It renders the canonical chain,
 *   which snap closure never reaches, so true scale is available there and is
 *   what it must keep. Plan and elevation answer different questions: the plan
 *   says how things sit relative to each other, the elevation says how big
 *   they are.
 *
 * Returns exactly 1 whenever there is nothing to correct or nothing to
 * correct it from — snap-close off, an unsolved room, a zero-length wall —
 * so a caller can multiply unconditionally.
 *
 * @param {number} drawnLengthPx     The wall's length as drawn, in drawing px.
 * @param {number} statedLengthUnits The wall's measured/solved length, in room units.
 * @param {number} pxPerUnit         The drawing's units→px scale.
 * @returns {number} A positive, finite factor; 1 when it cannot be determined.
 */
export function displayRunScale(drawnLengthPx, statedLengthUnits, pxPerUnit) {
  const truePx = statedLengthUnits * pxPerUnit;
  if (!(truePx > 0) || !(drawnLengthPx > 0)) return 1;
  const scale = drawnLengthPx / truePx;
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}
